/**
 * Site-Analytics admin tab (F100.1)
 *
 * Dashboard over /api/admin/analytics/summary + /api/admin/subscribers:
 * stat cards, views-over-time chart (recharts), source/country/page breakdowns,
 * the ?src= link builder, the subscriber list, and the DB backup download.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  getAnalyticsSummary, getSubscribers, downloadBackup, addSubscriber, deleteSubscriber, editSubscriber,
  getResumeLeads, resetTrafficData, getAliases, createAlias, deleteAlias,
  sendNewsletter, getNewsletterHistory, getNewsletterPosts,
} from '../services/analyticsApi';

const SOURCES = [
  { tag: 'li', label: 'LinkedIn' },
  { tag: 'yt', label: 'YouTube' },
  { tag: 'discord', label: 'Discord' },
  { tag: 'slack', label: 'Slack' },
  { tag: 'ig', label: 'Instagram' },
  { tag: 'tiktok', label: 'TikTok' },
  { tag: 'x', label: 'X / Twitter' },
];

/** Spell out ?src= tags (and "direct") in the Sources chart - no guessing. */
const sourceLabel = (source) => {
  if (source === 'direct') return 'Direct';
  const known = SOURCES.find((s) => s.tag === source);
  return known ? known.label : source; // referrer hosts (www.google.com) stay as-is
};

const StatCard = ({ label, value, hint }) => (
  <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4">
    <p className="text-xs uppercase tracking-wide text-neutral-400">{label}</p>
    <p className="mt-1 text-2xl font-bold text-neutral-100">{value}</p>
    {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
  </div>
);

/** Horizontal bar list (sources, countries, pages) - no chart lib needed. */
const BarList = ({ title, rows, nameKey }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4">
      <h3 className="text-sm font-semibold text-neutral-200 mb-3">{title}</h3>
      {rows.length === 0 && <p className="text-sm text-neutral-500">No data yet.</p>}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r[nameKey]} className="relative">
            <div
              className="absolute inset-y-0 left-0 bg-sky-500/15 rounded"
              style={{ width: `${(r.count / max) * 100}%` }}
            />
            <div className="relative flex justify-between px-2 py-1 text-sm">
              {/* break-all + no truncate: long post paths wrap to a 2nd line
                  instead of cutting off; tooltip shows the full value too */}
              <span className="text-neutral-300 break-all pr-2 min-w-0" title={r[nameKey]}>{r[nameKey]}</span>
              <span className="text-neutral-400 tabular-nums shrink-0">{r.count}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Visitor numbers: reach (unique people) vs depth (views per person). */
const VisitorsPanel = ({ summary }) => {
  const best = (summary.views_by_day || []).reduce(
    (acc, d) => ((d.visitors ?? 0) > (acc?.visitors ?? -1) ? d : acc),
    null
  );
  const rows = [
    { label: `Unique visitors (${summary.label ?? ''})`, value: summary.unique_visitors ?? 0 },
    { label: 'Visitors today (UTC)', value: summary.visitors_today ?? 0 },
    { label: 'Avg views per visitor', value: summary.views_per_visitor ?? 0 },
    { label: 'Best day', value: best && best.visitors ? `${best.day} · ${best.visitors} visitors` : '-' },
  ];
  return (
    <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4">
      <h3 className="text-sm font-semibold text-neutral-200 mb-1">Visitors</h3>
      <p className="text-xs text-neutral-500 mb-3">
        Unique people, counted by a daily anonymous hash - no cookies, not trackable across days.
      </p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.label} className="flex justify-between px-2 py-1 text-sm">
            <span className="text-neutral-400">{r.label}</span>
            <span className="text-neutral-200 font-medium tabular-nums">{r.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const LinkBuilder = () => {
  const [postUrl, setPostUrl] = useState('');
  const [alias, setAlias] = useState('');
  const [aliasEdited, setAliasEdited] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(null); // "alias:tag" or "latest:tag"
  const [pathMissing, setPathMissing] = useState(false);

  const cleanPath = useMemo(() => {
    // Forgiving input: a pasted full URL (with or without ?src=) reduces to its path.
    let clean = postUrl.trim();
    if (/^https?:\/\//i.test(clean)) {
      try { clean = new URL(clean).pathname; } catch (e) { /* keep as typed */ }
    }
    if (clean.includes('?')) clean = clean.split('?')[0];
    if (!clean.startsWith('/')) clean = `/${clean}`;
    return clean;
  }, [postUrl]);

  // Suggest an alias from the slug (last path segment, series prefix trimmed);
  // stops suggesting once the admin edits the alias field by hand.
  useEffect(() => {
    if (aliasEdited) return;
    const seg = cleanPath.split('/').filter(Boolean).pop() || '';
    setAlias(seg.replace(/^agentic-workflow-/, '').slice(0, 60));
  }, [cleanPath, aliasEdited]);

  const load = useCallback(() => {
    getAliases().then((r) => { if (r.success) setAliases(r.data.aliases); });
  }, []);
  useEffect(() => { load(); }, [load]);

  // Warn when the typed path does not exist on the site (same-origin HEAD probe).
  useEffect(() => {
    if (cleanPath === '/') { setPathMissing(false); return; }
    const probe = setTimeout(() => {
      fetch(cleanPath, { method: 'HEAD' })
        .then((resp) => setPathMissing(!resp.ok))
        .catch(() => setPathMissing(false));
    }, 400);
    return () => clearTimeout(probe);
  }, [cleanPath]);

  const save = async (e) => {
    e.preventDefault();
    if (!alias.trim() || cleanPath === '/') return;
    const result = await createAlias(cleanPath, alias.trim().toLowerCase());
    if (result.success) {
      setMsg(`Saved - /${result.data.alias} links are live (and bare links now point here).`);
      setPostUrl('');
      setAliasEdited(false);
      load();
    } else {
      setMsg(result.error);
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete the short links for "${a}"? Shared /tag/${a} links would fall back to home.`)) return;
    await deleteAlias(a);
    load();
  };

  const copyLink = (a, tag) => {
    const url = a === null
      ? `https://www.rajasleem.com/${tag}`
      : `https://www.rajasleem.com/${tag}/${a}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(`${a ?? 'latest'}:${tag}`);
      setTimeout(() => setCopied(null), 1500);
    }).catch(() => {});
  };

  const TagChips = ({ a }) => (
    <div className="flex flex-wrap gap-1.5">
      {SOURCES.map((s2) => (
        <button
          key={s2.tag}
          onClick={() => copyLink(a, s2.tag)}
          className="px-2 py-0.5 text-xs bg-neutral-900 border border-neutral-700 rounded-lg text-sky-300 hover:border-sky-600 transition-colors"
          title={`Copy ${a === null ? `rajasleem.com/${s2.tag} (always the latest post)` : `rajasleem.com/${s2.tag}/${a}`} - ${s2.label}`}
        >
          {copied === `${a ?? 'latest'}:${s2.tag}` ? 'Copied!' : `/${s2.tag}${a ? `/${a}` : ''}`}
        </button>
      ))}
    </div>
  );

  return (
    <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4">
      <h3 className="text-sm font-semibold text-neutral-200 mb-1">Share links</h3>
      <p className="text-xs text-neutral-500 mb-3">
        Paste a post URL, save it once - every channel gets a short, PERMANENT link
        (rajasleem.com/li/{'{'}name{'}'}) with the source tag baked into the path, so preview
        cards can't strip it. Old links never change where they point.
      </p>

      <form onSubmit={save} className="flex flex-col md:flex-row gap-2 mb-1">
        <input
          className="flex-1 bg-neutral-900 border border-neutral-600 rounded-lg px-3 py-2 text-sm text-neutral-200 focus:border-sky-500 outline-none"
          value={postUrl}
          onChange={(e) => setPostUrl(e.target.value)}
          placeholder="paste the post URL (or /posts/... path)"
        />
        <input
          className="md:w-56 bg-neutral-900 border border-neutral-600 rounded-lg px-3 py-2 text-sm text-neutral-200 focus:border-sky-500 outline-none"
          value={alias}
          onChange={(e) => { setAlias(e.target.value); setAliasEdited(true); }}
          placeholder="short name (auto)"
        />
        <button
          type="submit"
          className="px-4 py-2 text-sm bg-sky-600 hover:bg-sky-700 text-white rounded-lg transition-colors shrink-0"
        >
          Save
        </button>
      </form>
      {pathMissing && (
        <p className="mt-1 text-xs text-amber-400">
          Heads up: this path doesn't exist on the site - paste the full URL from your browser to be safe.
        </p>
      )}
      {msg && <p className="mt-1 text-xs text-neutral-400">{msg}</p>}

      {/* Saved posts, newest first; plus the always-latest bare links */}
      <ul className="mt-3 space-y-3">
        <li className="border-t border-neutral-700/60 pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-neutral-300">Always the latest post</span>
            <span className="text-xs text-neutral-500">follows whatever you save next</span>
          </div>
          <TagChips a={null} />
        </li>
        {aliases.map((row) => (
          <li key={row.alias} className="border-t border-neutral-700/60 pt-3">
            <div className="flex items-center justify-between mb-1.5 gap-2">
              <span className="text-sm text-neutral-300 break-all" title={row.path}>{row.path}</span>
              <button
                onClick={() => remove(row.alias)}
                className="px-2 py-0.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors shrink-0"
              >
                Delete
              </button>
            </div>
            <TagChips a={row.alias} />
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Subscriber list with admin management: add manually, delete per row. */
const SubscribersPanel = ({ subs, onChanged }) => {
  const [newEmail, setNewEmail] = useState('');
  const [msg, setMsg] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    const result = await addSubscriber(newEmail.trim());
    if (result.success) {
      setNewEmail('');
      setMsg(result.data.added ? 'Added.' : 'Already on the list.');
      onChanged();
    } else {
      setMsg(result.error);
    }
    setTimeout(() => setMsg(null), 3000);
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete ${email} from the list?`)) return;
    const result = await deleteSubscriber(email);
    setMsg(result.success ? 'Deleted.' : result.error);
    onChanged();
    setTimeout(() => setMsg(null), 3000);
  };

  const handleEdit = async (email) => {
    const newEmail = window.prompt('New email address:', email);
    if (!newEmail || newEmail.trim().toLowerCase() === email) return;
    const result = await editSubscriber(email, newEmail.trim());
    setMsg(result.success ? 'Updated.' : result.error);
    onChanged();
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-neutral-200">
          Subscribers {subs ? `(${subs.count})` : ''}
        </h3>
        <form onSubmit={handleAdd} className="flex items-center gap-2">
          {msg && <span className="text-xs text-neutral-400">{msg}</span>}
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="add email manually"
            className="bg-neutral-900 border border-neutral-600 rounded-lg px-3 py-1.5 text-sm text-neutral-200 focus:border-sky-500 outline-none w-52"
          />
          <button
            type="submit"
            className="px-3 py-1.5 text-sm bg-neutral-700 hover:bg-neutral-600 text-neutral-200 border border-neutral-600 rounded-lg transition-colors"
          >
            Add
          </button>
        </form>
      </div>
      {!subs || subs.count === 0 ? (
        <p className="text-sm text-neutral-500">No subscribers yet.</p>
      ) : (
        // overflow-x-auto: on phone widths the table scrolls sideways inside
        // the card instead of clipping the Edit/Delete actions off-screen.
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
              <th className="pb-2 font-medium">Email</th>
              <th className="pb-2 pl-3 font-medium">Subscribed</th>
              <th className="pb-2 pl-3 font-medium">From page</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {subs.subscribers.map((s) => (
              <tr key={s.email} className="border-t border-neutral-700/60">
                <td className="py-2 text-neutral-300">{s.email}</td>
                <td className="py-2 pl-3 text-neutral-400 whitespace-nowrap">{s.subscribed_at}</td>
                <td className="py-2 pl-3 text-neutral-400">{s.page || '-'}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => handleEdit(s.email)}
                    className="px-2 py-1 text-xs text-sky-400 hover:text-sky-300 hover:bg-sky-900/30 rounded transition-colors"
                    aria-label={`Edit ${s.email}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(s.email)}
                    className="ml-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors"
                    aria-label={`Delete ${s.email}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

/** Newsletter (F100.2): compose a new-post announcement, test-send to self,
 *  send to the whole list, and see the send history. */
const NewsletterPanel = ({ subs }) => {
  const [form, setForm] = useState({ subject: '', title: '', description: '', url: '' });
  const [posts, setPosts] = useState([]);
  const [pickedSlug, setPickedSlug] = useState('');
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadHistory = useCallback(async () => {
    const h = await getNewsletterHistory();
    if (h.success) setHistory(h.data.sends);
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    (async () => {
      const p = await getNewsletterPosts();
      if (p.success) setPosts(p.data.posts);
    })();
  }, []);

  // Picking a post is the template: subject, title, description, and the
  // PRODUCTION url all fill themselves; every field stays editable after.
  const pickPost = (e) => {
    const slug = e.target.value;
    setPickedSlug(slug);
    const p = posts.find((x) => x.slug === slug);
    if (p) {
      setForm({
        subject: `New post: ${p.title}`,
        title: p.title,
        description: p.description,
        url: p.url,
      });
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ready = form.subject.trim() && form.title.trim() && form.description.trim() && form.url.trim();
  const subCount = subs ? subs.count : 0;

  const doSend = async (test) => {
    if (!ready) return;
    if (!test) {
      if (!window.confirm(`Send to ALL ${subCount} subscriber(s)? Run "Send test to me" first.`)) return;
      if (!window.confirm('Really send to the whole list? This cannot be unsent.')) return;
    }
    setBusy(true);
    const result = await sendNewsletter({ ...form, test });
    setBusy(false);
    if (result.success) {
      setMsg(`${test ? 'Test sent' : 'Sent'}: ${result.data.sent} ok, ${result.data.failed} failed.`);
      loadHistory();
    } else {
      setMsg(result.error);
    }
    setTimeout(() => setMsg(null), 6000);
  };

  const inputCls = 'w-full bg-neutral-900 border border-neutral-600 rounded-lg px-3 py-2 text-sm text-neutral-200 focus:border-sky-500 outline-none';
  return (
    <div className="space-y-4">
      <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-neutral-200">Send a new-post announcement</h3>
          {msg && <span className="text-xs text-neutral-400">{msg}</span>}
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Post</label>
          <select value={pickedSlug} onChange={pickPost} className={inputCls} aria-label="Pick a post">
            <option value="">Pick a post (fills everything below)…</option>
            {posts.map((p) => (
              <option key={p.slug} value={p.slug}>{p.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Email subject (what the inbox shows)</label>
          <input value={form.subject} onChange={set('subject')} placeholder="New post: ..." className={inputCls} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Post title (email headline)</label>
          <input value={form.title} onChange={set('title')} placeholder="Post title" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Description (the hook under the headline)</label>
          <textarea value={form.description} onChange={set('description')} placeholder="One-paragraph description" rows={3} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Post URL (the Read button target)</label>
          <input value={form.url} onChange={set('url')} placeholder="https://www.rajasleem.com/posts/slug/" className={inputCls} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => doSend(true)}
            disabled={!ready || busy}
            className="px-3 py-1.5 text-sm bg-neutral-700 hover:bg-neutral-600 text-neutral-200 border border-neutral-600 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
            title="Sends only to your own email"
          >
            {busy ? 'Sending…' : 'Send test to me'}
          </button>
          <button
            onClick={() => doSend(false)}
            disabled={!ready || busy || subCount === 0}
            className="px-3 py-1.5 text-sm bg-emerald-700 hover:bg-emerald-600 text-white border border-emerald-600 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? 'Sending…' : `Send to all (${subCount})`}
          </button>
        </div>
        {/* Preview: rough render of the email card */}
        {ready && (
          <div className="border border-neutral-700 rounded-lg p-4 bg-neutral-100">
            <p className="text-xs text-neutral-500 mb-2">New post on rajasleem.com</p>
            <p className="text-lg font-bold text-neutral-900">{form.title}</p>
            <p className="text-sm text-neutral-700 mt-1 mb-3">{form.description}</p>
            <span className="inline-block bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">Read the post</span>
            <p className="text-[10px] tracking-widest font-bold text-emerald-700 mt-4">VIBE CODE AND PROSPER</p>
            <p className="text-[11px] text-neutral-400 mt-1">You get this because you subscribed at rajasleem.com. Unsubscribe</p>
          </div>
        )}
      </div>

      <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-neutral-200 mb-3">Send history</h3>
        {!history || history.length === 0 ? (
          <p className="text-sm text-neutral-500">Nothing sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                <th className="pb-2 font-medium">When</th>
                <th className="pb-2 pl-3 font-medium">Subject</th>
                <th className="pb-2 pl-3 font-medium">Type</th>
                <th className="pb-2 pl-3 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-neutral-700/60">
                  <td className="py-2 text-neutral-400 whitespace-nowrap">{h.sent_at}</td>
                  <td className="py-2 pl-3 text-neutral-300">{h.subject}</td>
                  <td className="py-2 pl-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${h.is_test ? 'bg-neutral-700 text-neutral-300' : 'bg-emerald-900/50 text-emerald-300'}`}>
                      {h.is_test ? 'test' : 'list'}
                    </span>
                  </td>
                  <td className="py-2 pl-3 text-neutral-400 whitespace-nowrap">{h.sent} ok{h.failed ? `, ${h.failed} failed` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminAnalyticsTab = () => {
  const [range, setRange] = useState('7d');
  const [section, setSection] = useState('analytics');
  const [chartMetric, setChartMetric] = useState('views');
  const [summary, setSummary] = useState(null);
  const [subs, setSubs] = useState(null);
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(null);
  const [backupMsg, setBackupMsg] = useState(null);

  const refreshSubs = useCallback(async () => {
    const sub = await getSubscribers();
    if (sub.success) setSubs(sub.data);
  }, []);

  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    const [s, sub, ld] = await Promise.all([
      getAnalyticsSummary(range), getSubscribers(), getResumeLeads(),
    ]);
    if (s.success) setSummary(s.data); else setError(s.error);
    if (sub.success) setSubs(sub.data);
    if (ld.success) setLeads(ld.data);
    setUpdatedAt(new Date().toLocaleTimeString());
    setRefreshing(false);
  }, [range]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleBackup = useCallback(async () => {
    setBackupMsg('Preparing backup...');
    const result = await downloadBackup();
    setBackupMsg(result.success ? 'Backup downloaded.' : result.error);
    setTimeout(() => setBackupMsg(null), 4000);
  }, []);

  const handleReset = useCallback(async () => {
    if (!window.confirm('Wipe ALL traffic data (views, chat counters)? Subscribers and resume leads are kept.')) return;
    if (!window.confirm('This cannot be undone (consider Download backup first). Really wipe?')) return;
    const result = await resetTrafficData();
    if (result.success) {
      setBackupMsg(`Wiped ${result.data.views} views.`);
      const s = await getAnalyticsSummary(range);
      if (s.success) setSummary(s.data);
    } else {
      setBackupMsg(result.error);
    }
    setTimeout(() => setBackupMsg(null), 4000);
  }, [range]);

  if (error) {
    return <p className="text-sm text-red-400">{error}</p>;
  }
  if (!summary) {
    return <p className="text-sm text-neutral-400">Loading analytics...</p>;
  }

  const SECTIONS = [
    { id: 'analytics', label: 'Page Analytics' },
    { id: 'links', label: 'Links' },
    { id: 'subs', label: 'Subscribers' },
    { id: 'newsletter', label: 'Newsletter' },
    { id: 'leads', label: 'Resume Leads' },
  ];

  return (
    <div className="space-y-4">
      {/* Top row: range picker left, utilities right - full width, above
          both the content and the rail so the rail aligns with the cards. */}
      {/* lg:pr-44 = rail width (w-40) + gap-4, so the buttons end flush with
          the content column (the Today card), not the rail's column. */}
      <div className="flex flex-wrap items-center justify-between gap-2 lg:pr-44">
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-1.5 text-sm text-neutral-200 outline-none"
          aria-label="Date range"
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="3d">Last 3 days</option>
          <option value="7d">Last 7 days</option>
          <option value="14d">Last 2 weeks</option>
          <option value="30d">Last month</option>
          <option value="90d">Last 3 months</option>
          <option value="180d">Last 6 months</option>
          <option value="year">This year</option>
          <option value="all">All time</option>
        </select>
        {/* flex-wrap + whitespace-nowrap: on phone widths the buttons wrap to
            extra rows instead of clipping off-screen; each label stays on one
            line. Desktop (one row) is unchanged. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {backupMsg && <span className="text-xs text-neutral-400">{backupMsg}</span>}
          {updatedAt && (
            <span className="text-xs text-neutral-500 whitespace-nowrap" title="Last data fetch (your local time)">
              Updated {updatedAt}
            </span>
          )}
          <button
            onClick={loadAll}
            disabled={refreshing}
            className="px-3 py-1.5 text-sm bg-neutral-800 hover:bg-neutral-700 text-neutral-200 border border-neutral-700 rounded-lg transition-colors disabled:opacity-60 whitespace-nowrap"
            title="Reload all dashboard data"
          >
            {refreshing ? 'Refreshing…' : '↻ Refresh'}
          </button>
          <a
            href="https://search.google.com/search-console?resource_id=sc-domain:rajasleem.com"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm bg-neutral-800 hover:bg-neutral-700 text-neutral-200 border border-neutral-700 rounded-lg transition-colors whitespace-nowrap"
          >
            Search Console ↗
          </a>
          <button
            onClick={handleBackup}
            className="px-3 py-1.5 text-sm bg-neutral-800 hover:bg-neutral-700 text-neutral-200 border border-neutral-700 rounded-lg transition-colors whitespace-nowrap"
          >
            Download backup
          </button>
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-sm bg-neutral-800 hover:bg-red-900/40 text-red-400 border border-red-900/60 rounded-lg transition-colors whitespace-nowrap"
            title="Wipe views and counters; subscribers and leads are kept"
          >
            Reset traffic
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-start">
      <div className="flex-1 min-w-0 space-y-4 w-full">
      {section === 'analytics' && (<>
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Page views" value={summary.total_views} />
        <StatCard label="Visitors" value={summary.unique_visitors ?? 0} hint="unique, daily-hashed" />
        <StatCard
          label="Chats started"
          value={summary.chats_started}
          hint={`${summary.chat_messages} msgs · ${summary.chats_started ? (summary.chat_messages / summary.chats_started).toFixed(1) : 0}/chat`}
        />
        <StatCard label="Resume requests" value={summary.resume_requests} />
        <StatCard label="Subscribers" value={summary.subscriber_count} hint={`+${summary.new_subscribers} · ${summary.label ?? ''}`} />
        <StatCard
          label="Today"
          value={summary.views_today ?? 0}
          hint={`views · ${summary.visitors_today ?? 0} visitors (UTC)`}
        />
      </div>

      {/* Traffic over time: Page views | Visitors */}
      <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-200">
            {chartMetric === 'views' ? 'Page views' : 'Visitors'} over time
          </h3>
          <div className="flex gap-1">
            {[
              { id: 'views', label: 'Page views' },
              { id: 'visitors', label: 'Visitors' },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setChartMetric(m.id)}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
                  chartMetric === m.id
                    ? 'bg-sky-600 text-white'
                    : 'bg-neutral-900 text-neutral-400 hover:text-neutral-200 border border-neutral-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        {summary.views_by_day.length === 0 ? (
          <p className="text-sm text-neutral-500">No views recorded yet.</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summary.views_by_day} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#3f3f3f" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} tick={{ fill: '#a3a3a3', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#3f3f3f' }} />
                <YAxis tick={{ fill: '#a3a3a3', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: '#171717', border: '1px solid #3f3f3f', borderRadius: 8, color: '#e2e8f0' }}
                  labelStyle={{ color: '#a3a3a3' }}
                />
                <Area type="monotone" dataKey={chartMetric} stroke="#38bdf8" strokeWidth={2} fill="url(#viewsFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Breakdowns: 2x2 - Visitors + Top pages, then Sources + Countries */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VisitorsPanel summary={summary} />
        <BarList title="Top pages (visitors)" rows={summary.top_paths} nameKey="path" />
        <BarList
          title="Sources (visitors)"
          rows={summary.sources.map((r) => ({ ...r, source: sourceLabel(r.source) }))}
          nameKey="source"
        />
        <BarList title="Countries (visitors)" rows={summary.countries} nameKey="country" />
      </div>

      </>)}

      {/* Links */}
      {section === 'links' && <LinkBuilder />}

      {/* Subscribers */}
      {section === 'subs' && <SubscribersPanel subs={subs} onChanged={refreshSubs} />}

      {/* Newsletter (F100.2): compose + send + history */}
      {section === 'newsletter' && <NewsletterPanel subs={subs} />}

      {/* Resume leads (form submissions - captured even when the email fails) */}
      {section === 'leads' && (
      <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-neutral-200 mb-3">
          Resume leads {leads ? `(${leads.count})` : ''}
        </h3>
        {!leads || leads.count === 0 ? (
          <p className="text-sm text-neutral-500">No resume requests yet.</p>
        ) : (
          // Same phone-width guard as the Subscribers table: scroll, don't clip.
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                <th className="pb-2 font-medium">Email</th>
                <th className="pb-2 font-medium">Name</th>
                <th className="pb-2 font-medium">Requested</th>
              </tr>
            </thead>
            <tbody>
              {leads.leads.map((l) => (
                <tr key={l.email + l.created_at} className="border-t border-neutral-700/60">
                  <td className="py-2 text-neutral-300">{l.email}</td>
                  <td className="py-2 text-neutral-400">{l.name || '-'}</td>
                  <td className="py-2 text-neutral-400 whitespace-nowrap">{l.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      )}
    </div>

    {/* Right rail: section tabs, parallel to the page. Mobile: a native
        dropdown above the content (nothing to overflow or clip). */}
    <nav className="order-first lg:order-last w-full lg:w-40 shrink-0 lg:sticky lg:top-4">
      <select
        value={section}
        onChange={(e) => setSection(e.target.value)}
        className="lg:hidden w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2.5 text-sm text-neutral-200 outline-none"
        aria-label="Analytics section"
      >
        {SECTIONS.map((t) => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>
      <ul className="hidden lg:flex lg:flex-col gap-1">
        {SECTIONS.map((t) => (
          <li key={t.id}>
            <button
              onClick={() => setSection(t.id)}
              className={`w-full text-left px-4 py-2.5 text-sm rounded-lg whitespace-nowrap transition-colors ${
                section === t.id
                  ? 'bg-sky-600/20 text-sky-300 border border-sky-700/50'
                  : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 border border-transparent'
              }`}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
    </div>
    </div>
  );
};

export default AdminAnalyticsTab;
